/**
 * SIP Service
 * WebRTC-based SIP calling using JsSIP
 */

import JsSIP from "jssip";
import type { RTCSession, EndEvent } from "jssip/lib/RTCSession";
import type {
  IncomingRTCSessionEvent,
  OutgoingRTCSessionEvent,
} from "jssip/lib/UA";
import { isIPv4 } from "is-ip";

// Enable all JsSIP debug output - this captures raw SIP messages
// (INVITE, 100 Trying, 180 Ringing, 200 OK, ACK, BYE, SDP, etc.)
JsSIP.debug.enable("JsSIP:*");

export const UA_EVENT = Object.freeze({
  CONNECTED: Symbol("connected"),
  DISCONNECTED: Symbol("disconnected"),
  REGISTERED: Symbol("registered"),
  UNREGISTERED: Symbol("unregistered"),
  REGISTRATION_FAILED: Symbol("registrationFailed"),
});

export const CALL_EVENTS = Object.freeze({
  PROGRESS: Symbol("progress"),
  FAILED: Symbol("failed"),
  ENDED: Symbol("ended"),
  CONFIRMED: Symbol("confirmed"),
});

interface ICallOptions {
  mediaConstraints: { audio: boolean; video: boolean };
  pcConfig: RTCConfiguration;
  eventHandlers: {
    progress: () => void;
    failed: (e: EndEvent) => void;
    ended: () => void;
    confirmed: () => void;
  };
}

const defaultCallOptions: ICallOptions = {
  mediaConstraints: { audio: true, video: false },
  pcConfig: {
    rtcpMuxPolicy: "require",
    // Reduce ICE candidate gathering time by using fewer STUN servers
    iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    // iceCandidatePoolSize: 0 to prevent pre-gathering which can slow things down
    iceCandidatePoolSize: 0,
  },
  eventHandlers: {
    progress: () => {},
    failed: () => {},
    ended: () => {},
    confirmed: () => {},
  },
};

export class CallEventHandler {
  progress: () => void;
  failed: (e: EndEvent) => void;
  ended: () => void;
  confirmed: () => void;

  constructor(
    progress: () => void,
    failed: (e: EndEvent) => void,
    ended: () => void,
    confirmed: () => void,
  ) {
    this.progress = progress;
    this.failed = failed;
    this.ended = ended;
    this.confirmed = confirmed;
  }
}

export class CallSession {
  session: RTCSession;

  constructor(session: RTCSession) {
    this.session = session;
  }

  isActive(): boolean {
    // JsSIP RTCSession status: 0=NULL, 1=INVITE_SENT, 2=1XX_RECEIVED, 3=INVITE_RECEIVED,
    // 4=WAITING_FOR_ANSWER, 5=ANSWERED, 6=WAITING_FOR_ACK, 7=CONFIRMED, 8=TERMINATED
    // We can only terminate if status is not 8 (TERMINATED)
    return this.session.status !== 8;
  }

  disconnect() {
    try {
      if (this.isActive()) {
        console.log("[SIPMessage] Terminating call session");
        console.log("[SIPMessage] Terminate called from:", new Error().stack);
        this.session.terminate();
      }
    } catch (error) {
      console.warn("[SIPMessage] Error terminating session:", error);
    }
  }

  sendDTMF(digit: string) {
    if (this.isActive()) {
      console.log("[SIPMessage] Sending DTMF", { digit });
      this.session.sendDTMF(digit);
    }
  }
}

interface ISIPConfig {
  registrationURI: string;
  contactURI: string;
  password: string;
  incomingCallHandler: CallEventHandler;
  extra_headers: string[];
}

// Create a logging wrapper for WebSocketInterface
class LoggingWebSocketInterface extends JsSIP.WebSocketInterface {
  constructor(url: string) {
    super(url);

    // Intercept the internal _ws after it's created
    const checkAndWrap = () => {
      // @ts-expect-error - accessing internal _ws property
      const ws = this._ws as WebSocket | undefined;
      // @ts-expect-error - using custom property to track wrapped sockets
      if (ws && !ws._sipLoggingWrapped) {
        // @ts-expect-error - marking as wrapped
        ws._sipLoggingWrapped = true;

        // Wrap send
        const originalSend = ws.send.bind(ws);
        ws.send = (data: string | ArrayBufferLike | Blob | ArrayBufferView) => {
          if (typeof data === "string") {
            console.log("[SIP] >>> SEND:", data);
          }
          return originalSend(data);
        };

        // Wrap onmessage
        const originalOnMessage = ws.onmessage;
        ws.onmessage = (event: MessageEvent) => {
          if (typeof event.data === "string") {
            console.log("[SIP] <<< RECV:", event.data);
          }
          if (originalOnMessage) {
            originalOnMessage.call(ws, event);
          }
        };
      }
    };

    // Check periodically until we can wrap the WebSocket
    const intervalId = setInterval(() => {
      // @ts-expect-error - accessing internal _ws property
      if (this._ws) {
        checkAndWrap();
        clearInterval(intervalId);
      }
    }, 10);

    // Clear after 5 seconds if not connected
    setTimeout(() => clearInterval(intervalId), 5000);
  }
}

export class SIP {
  private ua: JsSIP.UA;
  private currentSession: CallSession | null = null;
  private remoteAudio: HTMLAudioElement | null = null;

  constructor(config: ISIPConfig) {
    const socket = new LoggingWebSocketInterface(config.registrationURI);
    const configuration = {
      sockets: [socket],
      uri: config.contactURI,
      password: config.password,
      register: true,
      extra_headers: config.extra_headers,
    };

    this.ua = new JsSIP.UA(configuration);
    this.setupEventHandlers(config.incomingCallHandler);
  }

  private setupEventHandlers(incomingHandler: CallEventHandler) {
    this.ua.on(
      "newRTCSession",
      async (data: IncomingRTCSessionEvent | OutgoingRTCSessionEvent) => {
        const { session } = data;
        console.log("[SIPMessage] newRTCSession", {
          direction: session.direction,
          remoteIdentity: session.remote_identity?.uri?.toString(),
          localIdentity: session.local_identity?.uri?.toString(),
        });

        if (session.direction === "incoming") {
          console.log("[SIPMessage] Incoming call received", {
            from: session.remote_identity?.uri?.toString(),
          });
          this.currentSession = new CallSession(session);

          // Set up event handlers for incoming call with logging
          session.on("progress", () => {
            console.log("[SIPMessage] Incoming call progress (ringing)");
            incomingHandler.progress();
          });
          session.on("failed", (e: EndEvent) => {
            console.log("[SIPMessage] Incoming call failed", {
              cause: e.cause,
              originator: e.originator,
            });
            incomingHandler.failed(e);
          });
          session.on("ended", () => {
            console.log("[SIPMessage] Incoming call ended");
            incomingHandler.ended();
          });
          session.on("confirmed", () => {
            console.log("[SIPMessage] Incoming call confirmed (connected)");
            incomingHandler.confirmed();
          });

          try {
            console.log("[SIPMessage] Answering incoming call");
            session.answer({
              ...defaultCallOptions,
            });

            const localStream = await navigator.mediaDevices.getUserMedia({
              audio: true,
              video: false,
            });

            localStream.getTracks().forEach((track) => {
              session.connection.addTrack(track, localStream);
            });

            // Use modern ontrack event handler with proper typing
            session.connection.ontrack = (event: RTCTrackEvent) => {
              if (event.streams && event.streams[0]) {
                console.log(
                  "[SIPMessage] Remote audio track received (incoming)",
                );
                this.handleRemoteStream(event.streams[0]);
              }
            };
          } catch (error) {
            console.error("[SIPMessage] Error handling incoming call:", error);
            session.terminate();
          }
        }
      },
    );
  }

  private handleRemoteStream(stream: MediaStream) {
    // Clean up previous audio element
    if (this.remoteAudio) {
      this.remoteAudio.remove();
    }

    this.remoteAudio = document.createElement("audio");
    this.remoteAudio.srcObject = stream;
    this.remoteAudio.autoplay = true;
    document.body.appendChild(this.remoteAudio);
  }

  start() {
    console.log("[SIPMessage] Starting UA (User Agent)");
    this.ua.start();
  }

  stop() {
    console.log("[SIPMessage] Stopping UA (User Agent)");
    this.ua.stop();
    this.ua.terminateSessions();
    if (this.remoteAudio) {
      this.remoteAudio.remove();
      this.remoteAudio = null;
    }
  }

  on(event: symbol, callback: () => void) {
    switch (event) {
      case UA_EVENT.CONNECTED:
        this.ua.on("connected", () => {
          console.log("[SIPMessage] UA connected (WebSocket connected)");
          callback();
        });
        break;
      case UA_EVENT.DISCONNECTED:
        this.ua.on("disconnected", () => {
          console.log("[SIPMessage] UA disconnected (WebSocket disconnected)");
          callback();
        });
        break;
      case UA_EVENT.REGISTERED:
        this.ua.on("registered", () => {
          console.log(
            "[SIPMessage] UA registered (SIP registration successful)",
          );
          callback();
        });
        break;
      case UA_EVENT.UNREGISTERED:
        this.ua.on("unregistered", () => {
          console.log("[SIPMessage] UA unregistered");
          callback();
        });
        break;
      case UA_EVENT.REGISTRATION_FAILED:
        this.ua.on("registrationFailed", () => {
          console.log("[SIPMessage] UA registration failed");
          callback();
        });
        break;
      default:
        throw new Error("Invalid event");
    }
  }

  call(
    contactURI: string,
    callEventHandler: CallEventHandler,
    extraHeaders?: string[],
  ): CallSession {
    console.log("[SIPMessage] Initiating outgoing call", {
      to: contactURI,
      extraHeaders: extraHeaders,
    });

    const options = {
      ...defaultCallOptions,
      extraHeaders: extraHeaders || [],
      eventHandlers: {
        progress: () => {
          console.log("[SIPMessage] Outgoing call progress (ringing)", {
            to: contactURI,
          });
          callEventHandler.progress();
        },
        failed: (e: EndEvent) => {
          console.log("[SIPMessage] Outgoing call failed", {
            to: contactURI,
            cause: e.cause,
            originator: e.originator,
          });
          callEventHandler.failed(e);
        },
        ended: () => {
          console.log("[SIPMessage] Outgoing call ended", { to: contactURI });
          callEventHandler.ended();
        },
        confirmed: () => {
          console.log("[SIPMessage] Outgoing call confirmed (connected)", {
            to: contactURI,
          });
          callEventHandler.confirmed();
        },
      },
    };

    const session = this.ua.call(contactURI, options);
    this.currentSession = new CallSession(session);

    // Add detailed event listeners for debugging Android issues
    session.on("getusermediafailed", (error: Error) => {
      console.log("[SIPMessage] getUserMedia failed", {
        error: error.message,
        name: error.name,
      });
    });

    session.on("peerconnection:createofferfailed", (error: Error) => {
      console.log("[SIPMessage] createOffer failed", {
        error: error.message,
      });
    });

    session.on("peerconnection:createanswerfailed", (error: Error) => {
      console.log("[SIPMessage] createAnswer failed", {
        error: error.message,
      });
    });

    session.on("peerconnection:setlocaldescriptionfailed", (error: Error) => {
      console.log("[SIPMessage] setLocalDescription failed", {
        error: error.message,
      });
    });

    session.on("peerconnection:setremotedescriptionfailed", (error: Error) => {
      console.log("[SIPMessage] setRemoteDescription failed", {
        error: error.message,
      });
    });

    session.on(
      "icecandidate",
      (event: { candidate: RTCIceCandidate; ready: () => void }) => {
        const candidateStr = event.candidate?.candidate || null;

        // If candidate is null/empty, ICE gathering is complete
        if (!candidateStr) {
          console.log("[SIPMessage] ICE gathering complete");
          event.ready();
          return;
        }

        const address = event.candidate.address;

        const iPv4 = address ? isIPv4(address) : false;

        if (
          !iPv4 ||
          event.candidate.protocol === "tcp" ||
          event.candidate.type !== "srflx"
        ) {
          console.log("[SIPMessage] ICE candidate SKIPPED (IPv6/TCP)", {
            candidate: candidateStr,
            address,
            iPv4,
          });
          // Don't call ready() for skipped candidates
          return;
        }

        console.log(
          "[SIPMessage] ICE candidate USING (IPv4/UDP) - sending INVITE now!",
          {
            candidate: candidateStr,
            address,
          },
        );

        // Call ready() immediately on first good candidate to send INVITE without waiting
        // This implements "trickle ICE" behavior - send SDP with available candidates
        event.ready();
      },
    );

    // Use modern ontrack event handler with proper typing
    session.connection.ontrack = (event: RTCTrackEvent) => {
      if (event.streams && event.streams[0]) {
        console.log("[SIPMessage] Remote audio track received (outgoing)");
        this.handleRemoteStream(event.streams[0]);
      }
    };

    return this.currentSession;
  }

  getCurrentSession(): CallSession | null {
    return this.currentSession;
  }
}
